import { PropsWithChildren, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { FormContainer, useForm } from 'react-hook-form-mui';
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc';

import DragHandleIcon from '@mui/icons-material/DragHandle';
import { Box, IconButton, List as ListContainer, ListItem, ListItemIcon, Typography } from '@mui/material';

import { Accordion, Autocomplete, DeleteButton } from '../index';

interface PopulatedListProps<T> {
  items: T[];
  listItems: T[];
  isEqual: (a: T, b: T) => boolean;
  getOptionLabel: (option: T) => string;
  renderItem: (option: T) => ReactNode;
  onListItemChanged: (data: T[]) => void;
  accordionTitle: string;
  toggleAccordion: () => void;
  expanded: boolean;
  placeholder?: string;
  showError?: boolean;
  errorMessage?: string;
}

function PopulatedList<T>(props: PopulatedListProps<T>) {
  const {
    items,
    isEqual,
    getOptionLabel,
    renderItem,
    onListItemChanged,
    accordionTitle,
    toggleAccordion,
    expanded,
    placeholder,
    showError,
    errorMessage,
  } = props;

  const formContext = useForm<{ items: any[] }>({
    defaultValues: {
      items: [],
    },
  });

  const { watch, reset } = formContext;

  const [listItems, setListItems] = useState(props.listItems);

  const options = useMemo(() => items.filter((el) => !listItems.some((listItem) => isEqual(el, listItem))), [isEqual, items, listItems]);

  useEffect(() => {
    const subscription = watch((value) => {
      if (value.items!.length > 0) {
        setListItems((prevState) => [...prevState, ...value.items!]);
        reset();
      }
    });

    return () => subscription.unsubscribe();
  }, [reset, watch]);

  useEffect(() => {
    onListItemChanged(listItems);
  }, [listItems, onListItemChanged]);

  const onDeleteClick = useCallback(
    (item: T) => {
      setListItems((prevState) => prevState.filter((listItem) => !isEqual(item, listItem)));
    },
    [isEqual],
  );

  const onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    setListItems((prevState) => arrayMove(prevState, oldIndex, newIndex));
  };

  const DragHandle = useMemo(
    () =>
      SortableHandle(() => (
        <IconButton>
          <DragHandleIcon />
        </IconButton>
      )),
    [],
  );

  const Item = useMemo(
    () =>
      SortableElement<{ value: T }>(({ value }: { value: T }) => (
        <ListItem
          sx={{ pt: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr max-content', backgroundColor: 'background.paper' }}
          secondaryAction={<DeleteButton onClick={() => onDeleteClick(value)} />}
        >
          <ListItemIcon>
            <DragHandle />
          </ListItemIcon>
          {renderItem(value)}
        </ListItem>
      )),
    [DragHandle, onDeleteClick, renderItem],
  );

  const Container = SortableContainer<PropsWithChildren>(({ children }: { children: ReactNode }) => (
    <ListContainer sx={{ pt: 0 }}>{children}</ListContainer>
  ));

  return (
    <Accordion
      title={accordionTitle}
      onChange={toggleAccordion}
      expanded={expanded}
      titleAdornment={
        expanded && (
          <Box onClick={(event) => event.stopPropagation()} sx={{ flex: 0.3 }}>
            <FormContainer formContext={formContext}>
              <Autocomplete
                autocompleteProps={{ getOptionLabel }}
                textFieldProps={{ placeholder }}
                multiple
                name="items"
                options={options}
              />
            </FormContainer>
          </Box>
        )
      }
    >
      {showError && (
        <Typography variant="body2" color="error">
          {errorMessage}
        </Typography>
      )}
      <Container onSortEnd={onSortEnd} useDragHandle>
        {listItems.map((item, index) => (
          <Item key={getOptionLabel(item)} index={index} value={item} />
        ))}
      </Container>
    </Accordion>
  );
}

export default PopulatedList;
